import { Injectable } from '@angular/core';
import { CountryRegion } from '@models/countryRegion';
import { FormDataService } from '@services/form-data/form-data.service';
import { MdsOptionGeneric } from '@utility/utility';
import { BehaviorSubject, Observable, catchError, filter, map, shareReplay, tap } from 'rxjs';
import { Country, CountryViewModel } from 'src/app/models/country';

@Injectable({
    providedIn: 'root',
})
export class CountryStateService {
    constructor(private formService: FormDataService) {}

    private readonly countries = new BehaviorSubject<CountryViewModel[] | null>(null);
    private readonly activeCountries = new BehaviorSubject<Country[] | null>(null);
    private readonly regions = new BehaviorSubject<CountryRegion[] | null>(null);

    private activeLoading = false;

    public getCountries(): Observable<CountryViewModel[]> {
        if (this.countries.getValue() == null && this.activeLoading === false) {
            this.activeLoading = true;
            this.getDictionaryData()

                .subscribe();
        }

        return this.countries.asObservable().pipe(filter(Boolean));
    }

    public getCountriesStringArray(): Observable<string[]> {
        return this.getCountries().pipe(
            map((val) => {
                return val.map((country) => country.viewValue);
            })
        );
    }

    public getCountryByName(name: string): Observable<CountryViewModel> {
        return this.getCountries().pipe(map((countries) => countries.find((c) => c.viewValue === name)));
    }

    public getCountryById(id: number): Observable<CountryViewModel> {
        return this.getCountries().pipe(map((countries) => countries.find((c) => c.id === id)));
    }

    public getCountryByCode(code: string): Observable<CountryViewModel> {
        return this.getCountries().pipe(map((countries) => countries.find((c) => c.countryCode === code)));
    }

    public getCountryByCodeOrName(codeOrName: string): Observable<CountryViewModel> {
        if (codeOrName?.length === 3) return this.getCountryByCode(codeOrName);
        else return this.getCountryByName(codeOrName);
    }
    public getActiveCountries(): Observable<Country[]> {
        if (this.activeCountries.getValue() === null && this.activeLoading === false) {
            this.activeLoading = true;
            this.getDictionaryData().subscribe(() => (this.activeLoading = false));
        }

        return this.activeCountries.asObservable().pipe(filter(Boolean));
    }
    private readonly activeCountriesAsMdsOptions$ = this.activeCountries.asObservable().pipe(
        filter(Boolean),
        map((activeCountries) => {
            return activeCountries.map<MdsOptionGeneric<Country>>((country) => ({
                value: country,
                viewValue: country.name,
            }));
        }),
        shareReplay(1)
    );
    getActiveCountriesAsMdsOptions() {
        return this.activeCountriesAsMdsOptions$;
    }

    getRegions(): Observable<CountryRegion[]> {
        if (this.regions.getValue() === null && this.activeLoading === false) {
            this.activeLoading = true;
            this.getDictionaryData().subscribe();
        }

        return this.regions.asObservable();
    }

    public getRegionStateType(country: string): Observable<string> {
        return this.getRegions().pipe(
            map((regions) => {
                const reg = regions?.filter((reg) => reg.country === country)[0]?.region;
                return reg || 'Province / Region / State';
            })
        );
    }

    private mapToRegions(countries: Country[]) {
        return countries?.map((x) => <CountryRegion>{ country: x.name, region: x.regionType });
    }

    private mapToActiveCountries(countries: Country[]) {
        return countries?.filter((x) => x.isActive === true);
    }

    private mapToCountries(countries: Country[]) {
        return countries?.map(
            (x) =>
                new CountryViewModel({
                    id: x.id,
                    value: x.name,
                    viewValue: x.name,
                    countryCode: x.threeCharCode,
                    currencyIsoCode: x.currencyIsoCode,
                })
        );
    }

    private getDictionaryData() {
        return this.formService.getCountries().pipe(
            tap((res) => {
                const countries = this.mapToCountries(res);
                const activeCountries = this.mapToActiveCountries(res);
                const regions = this.mapToRegions(res);

                this.countries.next(countries);

                this.activeCountries.next(activeCountries);

                this.regions.next(regions);
            }),
            catchError((error) => {
                this.activeLoading = false;
                throw error;
            })
        );
    }
}
